import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, Subscription } from 'rxjs';
import { AppSelectors } from '../../app.state';
import { LogoutAction, normalizeInternalRedirect } from './auth.state.model';

@Injectable({ providedIn: 'root' })
export class AuthSyncService {
  private store = inject(Store);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private lastUrl = '/dashboard';

  init(): void {
    // Track the last visited non-login URL in memory (tab-local).
    const routerSub: Subscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      if (!event.urlAfterRedirects.includes('/login')) {
        this.lastUrl = event.urlAfterRedirects;
      }
    });

    const storageHandler = (event: StorageEvent) => {
      // access_token was removed in another tab → log out this tab too
      if (event.key === 'access_token' && event.newValue === null) {
        const isAuthenticated = this.store.selectSnapshot(AppSelectors.authState).isAuthenticated;

        if (isAuthenticated) {
          // Save the current URL synchronously BEFORE LogoutAction changes the router state
          if (!this.router.url.includes('/login')) {
            localStorage.setItem('redirect_url', this.router.url);
          }
          this.store.dispatch(new LogoutAction());
        }
      }

      // access_token was set in another tab → full reload to the last visited page
      if (event.key === 'access_token' && event.oldValue === null && event.newValue !== null) {
        const isAuthenticated = this.store.selectSnapshot(AppSelectors.authState).isAuthenticated;

        if (!isAuthenticated) {
          // Prefer the redirect_url saved during logout,
          // fall back to the in-memory lastUrl tracked by the router events
          const redirectUrl = normalizeInternalRedirect(localStorage.getItem('redirect_url'), this.lastUrl);
          localStorage.removeItem('redirect_url');
          // Navigate + full reload in one step: the browser loads the target URL fresh,
          // bootstrapping the entire Angular app from scratch with all auth data available
          // this is necessary for Chromium-based browsers
          window.location.href = redirectUrl;
        }
      }
    };

    window.addEventListener('storage', storageHandler);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('storage', storageHandler);
      routerSub.unsubscribe();
    });
  }
}
